import { memo, useCallback } from "react"
import { Box, Slider, TextField, Skeleton } from "@mui/material"
import { FILTER_LABELS } from "../../../constants"
import OutlinedDiv from "../OutlinedDiv"

function RangeFilter({ selector, modifiers: { options, selections, setSelections }, loading }) {
    const setFromValue = useCallback((newValue) => {
        setSelections(state => ({ ...state, [selector]: { ...state[selector], fromValue: newValue } }))
    }, [selector, setSelections])

    const setToValue = useCallback((newValue) => {
        setSelections(state => ({ ...state, [selector]: { ...state[selector], toValue: newValue } }))
    }, [selector, setSelections])

    return (
        <RangeFilterImpl
            label={FILTER_LABELS[selector]}
            minValue={options[selector].minValue}
            maxValue={options[selector].maxValue}
            fromValue={selections[selector].fromValue}
            loading={loading}
            setFromValue={setFromValue}
            toValue={selections[selector].toValue}
            setToValue={setToValue}
        />
    )
}

const RangeFilterImpl = memo(function RangeFilterImpl({
    label,
    minValue,
    maxValue,
    fromValue,
    setFromValue,
    toValue,
    setToValue,
    loading
}) {
    const handleFromChange = useCallback((event) => {
        const nextValue = event.target.value === '' ? minValue : Number(event.target.value)
        setFromValue(nextValue)
    }, [minValue, setFromValue])

    const handleFromBlur = useCallback(() => {
        if (fromValue < minValue) setFromValue(minValue)
        else if (fromValue > toValue) setFromValue(toValue)
    }, [fromValue, minValue, setFromValue, toValue])

    const handleToChange = useCallback((event) => {
        const nextValue = event.target.value === '' ? maxValue : Number(event.target.value)
        setToValue(nextValue)
    }, [maxValue, setToValue])

    const handleToBlur = useCallback(() => {
        if (toValue > maxValue) setToValue(maxValue)
        else if (toValue < fromValue) setToValue(fromValue)
    }, [fromValue, maxValue, setToValue, toValue])

    const handleSliderChange = useCallback((event, [fv, tv]) => {
        setFromValue(fv)
        setToValue(tv)
    }, [setFromValue, setToValue])

    return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <OutlinedDiv label={label} size="small">
                <Box sx={{ minWidth: 300, display: "flex", alignItems: 'center', px: 1, py: 0.5 }}>
                    {loading ? (
                        <Skeleton variant="rectangular" width="100%" height={32} sx={{ borderRadius: 0.5 }} />
                    ) : (
                        <>
                            <TextField
                                disabled={fromValue === Infinity}
                                value={fromValue}
                                size="small"
                                onChange={handleFromChange}
                                onBlur={handleFromBlur}
                                inputProps={{
                                    step: 0.01,
                                    min: minValue,
                                    max: maxValue,
                                    type: 'number',
                                    style: { width: 60, textAlign: 'center' }
                                }}
                                variant="standard"
                            />
                            <Box sx={{ ml: 2, mr: 2, display: "flex", flexGrow: 1 }}>
                                <Slider
                                    disabled={fromValue === Infinity}
                                    value={[fromValue, toValue]}
                                    onChange={handleSliderChange}
                                    min={minValue}
                                    max={maxValue}
                                    step={0.01}
                                    size="small"
                                />
                            </Box>
                            <TextField
                                disabled={toValue === Infinity}
                                value={toValue}
                                size="small"
                                onChange={handleToChange}
                                onBlur={handleToBlur}
                                inputProps={{
                                    step: 0.01,
                                    min: minValue,
                                    max: maxValue,
                                    type: 'number',
                                    style: { width: 60, textAlign: 'center' }
                                }}
                                variant="standard"
                            />
                        </>
                    )}
                </Box>
            </OutlinedDiv>
        </Box>
    )
})

export default RangeFilter
